import { Box, chakra, Flex, Text } from "@chakra-ui/react";
import { ReactNode } from "react";
import cardLogos from "./images/card_logos.svg";
import { cardInputProps, digitsOnly } from "./cardInput";
import { Alert } from "../Alert/Alert";

export interface CardFormErrors {
  cardNumber?: string;
  cvc?: string;
  name?: string;
  expDate?: string;
  authorize?: string;
  postalCode?: string;
}

// Standalone inputs (Cardholder Name, Zip) — full FormInput-style shell with
// its own border + focus ring. Matches PhoneField / FormInput exactly.
function StandaloneField({
  label,
  error,
  children,
}: {
  label: React.ReactNode;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <Box w="100%" mb="16px">
      <Text
        as="label"
        display="block"
        mb="6px"
        fontFamily="body"
        fontSize="13px"
        fontWeight="medium"
        color="fg.default"
        letterSpacing="-0.005em"
      >
        {label}
      </Text>
      <Flex
        align="center"
        w="100%"
        h="52px"
        bg="bg.canvas"
        borderRadius="md"
        borderWidth="1.5px"
        borderStyle="solid"
        borderColor={error ? "bg.danger" : "border.default"}
        transition="border-color 120ms, box-shadow 120ms"
        _focusWithin={{
          borderColor: error ? "bg.danger" : "border.brand",
          boxShadow: "0 0 0 4px {colors.bg.brandTint}",
        }}
        overflow="hidden"
      >
        {children}
      </Flex>
      {error && (
        <Text mt="6px" fontSize="12px" color="bg.danger" fontFamily="body">
          {error}
        </Text>
      )}
    </Box>
  );
}

interface CardFormLayoutProps {
  /**
   * The three inputs of the card cluster. Each provider supplies its own:
   * Authorize.net our text inputs, Stripe the iframes it hosts. The layout
   * owns everything around them — the shell, the hairlines, the card logos,
   * the name and zip fields, and where errors are shown.
   */
  cardNumber: ReactNode;
  expiry: ReactNode;
  cvc: ReactNode;
  name: string;
  onNameChange: (name: string) => void;
  postalCode: string;
  onPostalCodeChange: (postalCode: string) => void;
  errors?: CardFormErrors;
  onCancel?: () => void;
}

export const CardFormLayout = ({
  cardNumber,
  expiry,
  cvc,
  name,
  onNameChange,
  postalCode,
  onPostalCodeChange,
  errors,
  onCancel,
}: CardFormLayoutProps) => {
  const cardClusterError =
    errors?.cardNumber || errors?.cvc || errors?.expDate;

  return (
    <Box as="form" w="100%" display="flex" flexDirection="column">
      {errors?.authorize && (
        <Alert
          intent="danger"
          title={errors.authorize}
          style={{ marginBottom: "1rem" }}
        />
      )}

      {/* Card Information cluster: number on top, exp + cvc on the bottom
          row, all inside one bordered shell with hairlines splitting the
          rows / columns. Mirrors the prior visual but with GF tokens. */}
      <Box mb="16px" w="100%">
        <Flex justify="space-between" align="baseline" mb="6px">
          <Text
            as="label"
            fontFamily="body"
            fontSize="13px"
            fontWeight="medium"
            color="fg.default"
            letterSpacing="-0.005em"
          >
            Card Information
          </Text>
          {onCancel && (
            <chakra.button
              type="button"
              onClick={onCancel}
              bg="transparent"
              border="0"
              p="0"
              cursor="pointer"
              fontFamily="body"
              fontSize="12px"
              fontWeight="semibold"
              color="fg.brand"
              _hover={{ textDecoration: "underline" }}
            >
              Cancel
            </chakra.button>
          )}
        </Flex>
        <Box
          w="100%"
          bg="bg.canvas"
          borderRadius="md"
          borderWidth="1.5px"
          borderStyle="solid"
          borderColor={cardClusterError ? "bg.danger" : "border.default"}
          transition="border-color 120ms, box-shadow 120ms"
          _focusWithin={{
            borderColor: cardClusterError ? "bg.danger" : "border.brand",
            boxShadow: "0 0 0 4px {colors.bg.brandTint}",
          }}
          overflow="hidden"
        >
          {/* Card number row */}
          <Box position="relative">
            {cardNumber}
            <chakra.img
              src={cardLogos}
              alt=""
              h="32px"
              w="144px"
              position="absolute"
              right="8px"
              top="50%"
              transform="translateY(-50%)"
              pointerEvents="none"
            />
          </Box>
          {/* Hairline between number and exp/cvc row */}
          <Box h="1px" bg="border.default" />
          {/* Exp / CVC row */}
          <Flex>
            <Box w="50%">{expiry}</Box>
            <Box w="1px" bg="border.default" />
            <Box w="50%">{cvc}</Box>
          </Flex>
        </Box>
        {cardClusterError && (
          <Text mt="6px" fontSize="12px" color="bg.danger" fontFamily="body">
            {cardClusterError}
          </Text>
        )}
      </Box>

      <StandaloneField label="Cardholder Name" error={errors?.name}>
        <chakra.input
          type="text"
          aria-label="Cardholder Name"
          value={name}
          {...cardInputProps}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            onNameChange(e.target.value)
          }
        />
      </StandaloneField>

      <StandaloneField label="Zip Code" error={errors?.postalCode}>
        <chakra.input
          type="text"
          aria-label="Zip Code"
          autoComplete="billing postal-code"
          value={postalCode}
          {...cardInputProps}
          onKeyDown={digitsOnly}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            onPostalCodeChange(e.target.value)
          }
        />
      </StandaloneField>
    </Box>
  );
};
